import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, addDoc, deleteDoc, updateDoc, query } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { InputField, Modal } from './SharedComponents';
import { PlusCircle, Edit, Trash2 } from 'lucide-react';

const TiposInfraccion = () => {
    const [infracciones, setInfracciones] = useState([]);
    const [filtro, setFiltro] = useState(''); // Estado para el buscador unificado
    const [newInfraccion, setNewInfraccion] = useState({ codigo: '', descripcion: '', monto: '', gravedad: 'Leve' });
    const [editingInfraccion, setEditingInfraccion] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [infraccionToDelete, setInfraccionToDelete] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });

    useEffect(() => {
        const path = `artifacts/${appId}/public/data/tipos_infraccion`;
        const q = query(collection(db, path));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true, sensitivity: 'base' }));
            setInfracciones(data);
        });
        return () => unsubscribe();
    }, []);

    const handleEditClick = (infraccion) => {
        setEditingInfraccion(infraccion);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (infraccion) => {
        setInfraccionToDelete(infraccion);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!infraccionToDelete) return;
        try {
            const path = `artifacts/${appId}/public/data/tipos_infraccion`;
            await deleteDoc(doc(db, path, infraccionToDelete.id));
            setMessage({ text: 'Infracción eliminada.', type: 'success' });
        } catch (error) {
            setMessage({ text: 'Error al eliminar.', type: 'error' });
            console.error(error);
        } finally {
            setIsDeleteModalOpen(false);
            setInfraccionToDelete(null);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!editingInfraccion) return;
        try {
            const path = `artifacts/${appId}/public/data/tipos_infraccion`;
            const docRef = doc(db, path, editingInfraccion.id);
            await updateDoc(docRef, {
                ...editingInfraccion,
                monto: Number(editingInfraccion.monto)
            });
            setMessage({ text: 'Infracción actualizada.', type: 'success' });
        } catch (error) {
            setMessage({ text: 'Error al actualizar.', type: 'error' });
            console.error(error);
        } finally {
            setIsEditModalOpen(false);
            setEditingInfraccion(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const path = `artifacts/${appId}/public/data/tipos_infraccion`;
            await addDoc(collection(db, path), { ...newInfraccion, monto: Number(newInfraccion.monto) });
            setMessage({ text: 'Infracción agregada.', type: 'success' });
            setNewInfraccion({ codigo: '', descripcion: '', monto: '', gravedad: 'Leve' });
        } catch (error) {
            setMessage({ text: 'Error al agregar.', type: 'error' });
            console.error(error);
        }
    };

    // Filtrar infracciones por código O descripción
    const infraccionesFiltradas = infracciones.filter(inf =>
        inf.codigo.toLowerCase().includes(filtro.toLowerCase()) ||
        inf.descripcion.toLowerCase().includes(filtro.toLowerCase())
    );
    
    return (
        <div className="space-y-8">
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                <h3 className="text-xl font-semibold mb-4">Editar Infracción</h3>
                {editingInfraccion && (
                    <form onSubmit={handleUpdateSubmit} className="space-y-4">
                        <InputField label="Código" value={editingInfraccion.codigo} onChange={(e) => setEditingInfraccion({...editingInfraccion, codigo: e.target.value})} required />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                            <textarea value={editingInfraccion.descripcion} onChange={(e) => setEditingInfraccion({...editingInfraccion, descripcion: e.target.value})} required className="w-full p-2 border border-gray-300 rounded-md shadow-sm"></textarea>
                        </div>
                        <InputField label="Monto (S/)" type="number" value={editingInfraccion.monto} onChange={(e) => setEditingInfraccion({...editingInfraccion, monto: e.target.value})} required />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gravedad</label>
                            <select value={editingInfraccion.gravedad} onChange={(e) => setEditingInfraccion({...editingInfraccion, gravedad: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                                <option value="Leve">Leve</option>
                                <option value="Grave">Grave</option>
                                <option value="Muy Grave">Muy Grave</option>
                            </select>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Guardar Cambios</button>
                        </div>
                    </form>
                )}
            </Modal>
            <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                 <h3 className="text-xl font-semibold mb-4">Confirmar Eliminación</h3>
                 <p>¿Está seguro de que desea eliminar la infracción "{infraccionToDelete?.codigo}"? Esta acción no se puede deshacer.</p>
                 <div className="flex justify-end gap-3 mt-6">
                    <button type="button" onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Eliminar</button>
                </div>
            </Modal>

            {message.text && (
                <div className={`p-3 mb-4 rounded-md text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}
            
            <div className="p-6 border rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Agregar Nueva Infracción</h3>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <InputField label="Código" name="codigo" value={newInfraccion.codigo} onChange={(e) => setNewInfraccion({...newInfraccion, codigo: e.target.value})} required />
                    <InputField label="Descripción" name="descripcion" value={newInfraccion.descripcion} onChange={(e) => setNewInfraccion({...newInfraccion, descripcion: e.target.value})} required />
                    <InputField label="Monto (S/)" name="monto" type="number" value={newInfraccion.monto} onChange={(e) => setNewInfraccion({...newInfraccion, monto: e.target.value})} required />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gravedad</label>
                        <select name="gravedad" value={newInfraccion.gravedad} onChange={(e) => setNewInfraccion({...newInfraccion, gravedad: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                            <option value="Leve">Leve</option>
                            <option value="Grave">Grave</option>
                            <option value="Muy Grave">Muy Grave</option>
                        </select>
                    </div>
                    <button type="submit" className="md:col-span-4 w-full flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700">
                        <PlusCircle className="w-5 h-5" /> Agregar Infracción
                    </button>
                </form>
            </div>
            
            <div>
                <h3 className="text-xl font-semibold mb-4">Listado de Infracciones</h3>
                <div className="mb-4">
                    <InputField
                        placeholder="Buscar por Código o Descripción..."
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                    />
                </div>
                <div className="overflow-x-auto border rounded-md">
                     <table className="min-w-full bg-white">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gravedad</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {infraccionesFiltradas.map(inf => (
                                <tr key={inf.id}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{inf.codigo}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{inf.descripcion}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{inf.gravedad}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">S/ {Number(inf.monto).toFixed(2)}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm space-x-2">
                                        <button onClick={() => handleEditClick(inf)} className="p-1 text-blue-600 hover:text-blue-800"><Edit size={16}/></button>
                                        <button onClick={() => handleDeleteClick(inf)} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={16}/></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TiposInfraccion;
